import { gql } from "@apollo/client";

/**
 * Shared by the read and the write: both operations return a whole
 * `NearbyAlertsSettings`, so a save refreshes the screen without a second round
 * trip. Kept as one fragment because the two drifting apart is how a saved
 * field stops appearing until the next manual refresh.
 *
 * `config.levels` is deliberately not selected. The per-level notification
 * budgets are not edited here, and asking for a field this screen does not
 * render only widens what a change to it can break.
 */
export const NEARBY_ALERTS_FIELDS = gql`
  fragment NearbyAlertsFields on NearbyAlertsSettings {
    config {
      enabled
      businessRadiusMeters
      dwellMinutes
      stationarySpeedMps
      scanIntervalSeconds
      proximityRadiusMeters
      nearbyLimit
      nearbySearchRadiusMeters
      perBusinessCooldownDays
      quietHoursStartHour
      quietHoursEndHour
    }
    killSwitchEnabled
    effectiveEnabled
    usingDefaults
    warnings
    updatedAt
    bounds {
      field
      min
      max
      defaultValue
      unit
      integer
      description
    }
  }
`;

/**
 * The tuning, plus the range and unit of every field.
 *
 * `bounds` is not decoration: it is what the form renders itself from, so the
 * inputs and the server's validator cannot drift apart. Never hardcode a range
 * in the dashboard — a bound changed on the server has to reach this screen by
 * itself, or the form starts rejecting values the API would accept.
 *
 * These operations sit on the default tiers rather than `admin-export`, so
 * unlike the rate-limit screen this one is not at risk of throttling an admin
 * out of their own kill switch. It still does not poll — nothing here changes
 * except when an admin changes it.
 */
export const NEARBY_ALERTS_SETTINGS = gql`
  query NearbyAlertsSettings {
    nearbyAlertsSettings {
      ...NearbyAlertsFields
    }
  }
  ${NEARBY_ALERTS_FIELDS}
`;

/**
 * The raw `AppConfig` key/value table — how the minimum app version is read.
 *
 * This is the same public query the mobile app calls at launch, so what this
 * screen shows is literally what a device will resolve. There is no dedicated
 * read for `minVersion`, and none was needed.
 *
 * `id` is selected so these rows normalise in the cache the same way the
 * leaderboard's copy of this query stores them; two shapes of the same object
 * under one root field is how a cached read starts missing a field.
 */
export const APP_CONFIG_ROWS = gql`
  query AppConfigRows {
    fetchAppConfig {
      id
      key
      value
    }
  }
`;

/**
 * Not read from `fetchAppConfig`: the server leaves this row out of that public
 * read, and what is in force can come from its environment rather than a row.
 */
export const RECAPTCHA_SETTINGS = gql`
  query RecaptchaSettings {
    recaptchaSettings {
      scoreThreshold
      setFromDashboard
      serverDefault
      min
      max
      updatedAt
      warning
    }
  }
`;

/** Not read from `fetchAppConfig`, for the same reasons as the reCAPTCHA score above. */
export const CRM_EMAIL_SEND_SETTINGS = gql`
  query CrmEmailSendSettings {
    crmEmailSendSettings {
      perRun
      setFromDashboard
      serverDefault
      min
      max
      sendCron
      sendingEnabled
      updatedAt
      warning
    }
  }
`;
